import React, { useCallback, useState } from 'react';
import { useLazyQuery, useQuery, useNavigate } from '@common/hooks';
import {
  buildOptionsFromData,
  ErrorHandler,
  FilteredTable,
  MultiSelectTableFilter,
  Text,
  TextSearchTableFilter,
  TimeWithTimeZone,
} from '@common/components';
import {
  BILLING_CARDS_BY_ORGANIZATION_UNITS_AND_ROLES_QUERY,
  BillingCardCollectionType,
  ORGANIZATION_UNITS_QUERY,
} from '@institutions/graphql';
import { toISODateFormat } from 'works/legacy/lib/date-helper';
import Filters from 'works/legacy/lib/filters';
import { RolesQuery } from '@clinician-profile/graphql';
import { RoleLevelsQuery } from 'works/legacy/graphql/role-level';
import {
  normalizeSortingData,
  openInfoDrawerWhenRateCardExists,
  FiltersData,
  SelectedFilters,
  FilterOption,
  TableChangeParams,
  SortingData,
} from './_rates-long-term-service';
import { roleLevelParser } from '@institutions/services';
import { RoleLevelEnum } from '@common/graphql';

const LOCALSTORAGE_FILTER_KEY = 'RatesManagementLongTermRates';

const tableColumnsFilterable = ({
  selectedFilters,
  filtersData,
}: {
  selectedFilters: SelectedFilters;
  filtersData: FiltersData;
}) => {
  const uniqByClinicalUnitId = ({
    clinicalUnit: { id },
  }: {
    clinicalUnit: { id: string };
  }) => id;

  const uniqByBusinessId = ({
    department: {
      organizationBusiness: { id },
    },
  }: {
    department: {
      organizationBusiness: { id: string };
    };
  }) => id;

  const clinicalUnitToFilterOption = ({
    clinicalUnit: { id, displayName },
  }: {
    clinicalUnit: { id: string; displayName: string };
  }): FilterOption => ({
    text: displayName,
    value: id,
  });

  const businessToFilterOption = ({
    department: {
      organizationBusiness: { id, name },
    },
  }: {
    department: {
      organizationBusiness: { id: string; name: string };
    };
  }): FilterOption => ({
    text: name,
    value: id,
  });

  const roleAbbreviationToFilterOption = ({
    id,
    abbreviation,
  }: {
    id: string;
    abbreviation: string;
  }): FilterOption => ({
    text: abbreviation,
    value: id,
  });

  const uniqByItself = (it: unknown) => it;
  const levelToFilterOption = (level: RoleLevelEnum): FilterOption => ({
    text: roleLevelParser(level),
    value: level,
  });
  const noSorting = () => false;

  return [
    {
      key: 'unitName',
      dataIndex: ['organizationUnit', 'name'],
      title: 'Unit Name',
      sorter: {
        multiple: 1,
      },
      filtered: selectedFilters.unitName || false,
      filteredValue: selectedFilters.unitName || null,
      filterDropdown: TextSearchTableFilter,
    },
    {
      key: 'business',
      dataIndex: [
        'organizationUnit',
        'department',
        'organizationBusiness',
        'name',
      ],
      title: 'Facility',
      sorter: {
        multiple: 4,
      },
      filtered: selectedFilters.business || false,
      filteredValue: selectedFilters.business || null,
      filters: buildOptionsFromData(
        filtersData.organizationUnits || [],
        businessToFilterOption,
        uniqByBusinessId
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      key: 'costCenter',
      dataIndex: ['organizationUnit', 'costCenter'],
      title: 'Cost Center',
      sorter: {
        multiple: 3,
      },
      filtered: selectedFilters.costCenter || false,
      filteredValue: selectedFilters.costCenter || null,
      filterDropdown: TextSearchTableFilter,
    },
    {
      key: 'role',
      dataIndex: ['role', 'abbreviation'],
      title: 'Role',
      sorter: {
        multiple: 5,
      },
      filtered: selectedFilters.role || false,
      filteredValue: selectedFilters.role || null,
      filters: buildOptionsFromData(
        filtersData.roles || [],
        roleAbbreviationToFilterOption
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (value: string) => {
        return (
          <Text bold color="elm">
            {value}
          </Text>
        );
      },
    },
    {
      key: 'level',
      dataIndex: 'roleLevel',
      title: 'Level',
      sorter: {
        multiple: 6,
      },
      render: roleLevelParser,
      filtered: selectedFilters.level || false,
      filteredValue: selectedFilters.level || null,
      filters: buildOptionsFromData(
        filtersData.roleLevels || [],
        levelToFilterOption,
        uniqByItself,
        noSorting
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      key: 'clinicalUnit',
      dataIndex: ['organizationUnit', 'clinicalUnit', 'displayName'],
      title: 'Specialty (Clinical Unit)',
      sorter: {
        multiple: 2,
      },
      filtered: selectedFilters.clinicalUnit || false,
      filteredValue: selectedFilters.clinicalUnit || null,
      filters: buildOptionsFromData(
        filtersData.organizationUnits || [],
        clinicalUnitToFilterOption,
        uniqByClinicalUnitId
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      key: 'lastUpdated',
      dataIndex: ['billingCard', 'createdAt'],
      title: 'Last Updated',
      sorter: {
        multiple: 7,
      },
      render: (value: string) =>
        value && <TimeWithTimeZone date={value} variant="long_format" />,
    },
  ];
};

export function RatesLongTermTable({
  effectiveDate,
}: {
  effectiveDate?: string;
}) {
  const navigate = useNavigate();
  const formattedEffectiveDate = toISODateFormat(effectiveDate);

  const [selectedFilters, setSelectedFilters] = useState(
    Filters.getFilters(LOCALSTORAGE_FILTER_KEY)
  );

  const onFilterChange = (newFilters: SelectedFilters) => {
    setSelectedFilters(newFilters);
    Filters.setFilters(LOCALSTORAGE_FILTER_KEY, newFilters);
  };

  const [fetchUnits, { data: unitFilterData, error: unitsError }] =
    useLazyQuery(ORGANIZATION_UNITS_QUERY, {
      fetchPolicy: 'cache-first',
    });

  const [fetchRoleFilter, { data: roleFilterData, error: roleFilterError }] =
    useLazyQuery(RolesQuery, {
      fetchPolicy: 'cache-first',
    });

  const [fetchRoleLevel, { data: roleLevelFilterData, error: roleLevelError }] =
    useLazyQuery(RoleLevelsQuery, {
      fetchPolicy: 'cache-first',
    });

  const { loading, error, fetchMore, data } = useQuery(
    BILLING_CARDS_BY_ORGANIZATION_UNITS_AND_ROLES_QUERY,
    {
      skip: !effectiveDate,
      variables: {
        input: {
          effectiveDate: formattedEffectiveDate,
          filters: selectedFilters,
        },
      },
      onCompleted() {
        fetchUnits();
        fetchRoleFilter();
        fetchRoleLevel();
      },
    }
  );

  const filtersDataReady =
    !!unitFilterData && !!roleFilterData && !!roleLevelFilterData;

  function handleTableChange(
    pagination: TableChangeParams,
    filters: SelectedFilters,
    sortingData: SortingData | SortingData[]
  ) {
    fetchMore({
      variables: {
        input: {
          effectiveDate: formattedEffectiveDate,
          orders: {
            ...normalizeSortingData(sortingData),
          },
          filters,
          pagination: {
            page: pagination.current,
            pageSize: pagination.pageSize,
          },
        },
      },
      updateQuery: (_prev, { fetchMoreResult }) => {
        return fetchMoreResult;
      },
    });
  }

  const tableColumnsWithFilters = useCallback(
    () =>
      tableColumnsFilterable({
        selectedFilters,
        filtersData: {
          organizationUnits: unitFilterData?.organizationUnits,
          roles: roleFilterData?.roles,
          roleLevels: roleLevelFilterData?.roleLevels,
        },
      }),
    [
      selectedFilters,
      unitFilterData?.organizationUnits,
      roleFilterData?.roles,
      roleLevelFilterData?.roleLevels,
    ]
  );

  const anyError = error || unitsError || roleFilterError || roleLevelError;
  if (anyError) {
    return <ErrorHandler error={anyError} log="RatesLongTermList" />;
  }

  return (
    <>
      <section>
        <FilteredTable
          rowKey="uniqueKey"
          className="long-term-rates-list"
          filtersDataReady={filtersDataReady}
          columns={tableColumnsWithFilters()}
          dataSource={data?.billingCardsByOrganizationUnitsAndRoles?.collection}
          loading={loading}
          onChange={handleTableChange}
          onRow={(record: BillingCardCollectionType) => ({
            onClick: openInfoDrawerWhenRateCardExists(record, navigate),
          })}
          pagination={{
            total:
              data?.billingCardsByOrganizationUnitsAndRoles?.metadata
                ?.totalCount,
          }}
          localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
          onFilterChange={onFilterChange}
          filters={selectedFilters}
        />
      </section>
      <style jsx>{`
        }
        :global(.long-term-rates-list .ant-table-tbody > tr > td) {
          padding: 18px 4px 17px 8px;
        }
      `}</style>
    </>
  );
}
